import type { TFunction } from 'i18next';
import { Link } from 'react-router';
import { RowActions, StatusBadge, type DataTableColumn, type RowActionItem } from '@/components/ui';
import type { AdminAccountSummary } from '../types/admin';
import { AdminRoleChips } from './AdminRoleChips';

type TFn = TFunction<'common', undefined>;

const STATUS_VARIANT: Record<string, 'success' | 'danger' | 'neutral'> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  SUSPENDED: 'danger',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
};

interface BuildColumnsArgs {
  t: TFn;
  canWrite: boolean;
  onEdit: (adminUserId: string) => void;
  onChangeRoles: (account: AdminAccountSummary) => void;
  onDeactivate: (account: AdminAccountSummary) => void;
}

export const buildAdminAccountColumns = ({
  t,
  canWrite,
  onEdit,
  onChangeRoles,
  onDeactivate,
}: BuildColumnsArgs): ReadonlyArray<DataTableColumn<AdminAccountSummary>> => [
  {
    key: 'name',
    header: t('admin.accounts.cols.name'),
    cell: (row) => (
      <Link
        to={`/admin/accounts/${row.adminUserId}`}
        className="font-medium underline-offset-2 hover:underline"
        style={{ color: 'var(--content-strong)' }}
      >
        {row.name}
      </Link>
    ),
    width: 140,
  },
  {
    key: 'email',
    header: t('admin.accounts.cols.email'),
    cell: (row) => (
      <span
        className="block overflow-hidden text-ellipsis whitespace-nowrap text-[12px]"
        style={{ color: 'var(--content-default)' }}
        title={`${row.email} (${row.loginId})`}
      >
        {row.email}
        <span className="ml-1" style={{ color: 'var(--content-muted)' }}>
          ({row.loginId})
        </span>
      </span>
    ),
  },
  {
    key: 'roles',
    header: t('admin.accounts.cols.roles'),
    cell: (row) => <AdminRoleChips roles={row.roles} />,
    width: 240,
  },
  {
    key: 'status',
    header: t('admin.accounts.cols.status'),
    cell: (row) => (
      <StatusBadge
        label={t(`admin.accounts.status.${row.status}`, row.status)}
        variant={STATUS_VARIANT[row.status] ?? 'neutral'}
      />
    ),
    width: 96,
  },
  {
    key: 'twoFactorEnabled',
    header: t('admin.accounts.cols.twoFactor'),
    cell: (row) => (
      <StatusBadge
        label={
          row.twoFactorEnabled
            ? t('admin.accounts.twoFactor.enabled')
            : t('admin.accounts.twoFactor.disabled')
        }
        variant={row.twoFactorEnabled ? 'success' : 'neutral'}
        size="sm"
      />
    ),
    width: 104,
  },
  {
    key: 'lastLoginAt',
    header: t('admin.accounts.cols.lastLoginAt'),
    cell: (row) => (
      <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
        {formatDate(row.lastLoginAt)}
      </span>
    ),
    width: 152,
  },
  {
    key: 'actions',
    header: '',
    align: 'right',
    width: 56,
    cell: (row) => {
      const actions: RowActionItem[] = [
        {
          key: 'edit',
          label: t('admin.accounts.actions.edit'),
          disabled: !canWrite,
          onClick: () => onEdit(row.adminUserId),
        },
        {
          key: 'roles',
          label: t('admin.accounts.actions.changeRoles'),
          disabled: !canWrite,
          onClick: () => onChangeRoles(row),
        },
      ];
      if (row.status === 'ACTIVE') {
        actions.push({
          key: 'deactivate',
          label: t('admin.accounts.actions.deactivate'),
          variant: 'danger',
          separatorBefore: true,
          disabled: !canWrite,
          onClick: () => onDeactivate(row),
        });
      }
      return (
        <RowActions triggerLabel={t('admin.accounts.actions.menuAria')} actions={actions} />
      );
    },
  },
];
